import { useState } from "react";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { EditIcon, FolderIcon, SortIcon, TrashIcon } from "@icons";
import { OpenMenuCell } from "./MenuPlaygroundCell";
import {
    StatefulRadioGroup,
    StatefulToggleItem,
} from "./MenuPlaygroundStateful";
import { VariantGrid, VariantItem } from "./playgroundControls";

const sortOptions = [
    { value: "name", label: "Name" },
    { value: "date", label: "Date" },
    { value: "amount", label: "Amount" },
] as const;

const SortRadioItems = () =>
    sortOptions.map(({ value, label }) => (
        <Menu.RadioItem key={value} value={value}>
            {label}
        </Menu.RadioItem>
    ));

/** A real (not forced-open) menu whose choices live outside the popup. */
const LiveSortMenu = () => {
    const [sort, setSort] = useState("date");
    const [isShowingVoided, setIsShowingVoided] = useState(false);
    const sortLabel = sortOptions.find(({ value }) => value === sort)?.label;

    return (
        <Menu.Root
            trigger={
                <Button variant="secondary" leftIcon={<SortIcon />}>
                    Sort: {sortLabel}
                </Button>
            }
        >
            <Menu.RadioGroup value={sort} onValueChange={setSort}>
                <Menu.GroupLabel>Sort by</Menu.GroupLabel>
                <SortRadioItems />
            </Menu.RadioGroup>
            <Menu.Separator />
            <Menu.ToggleItem
                checked={isShowingVoided}
                onCheckedChange={setIsShowingVoided}
            >
                Show voided
            </Menu.ToggleItem>
        </Menu.Root>
    );
};

/**
 * Submenus, toggle rows and radio groups: forced-open examples for
 * screenshots plus one live menu that closes on a radio pick.
 */
export const MenuPlaygroundSelection = () => (
    <VariantGrid title="Submenus, toggles and radios" columns="three">
        <OpenMenuCell label="Menu.Submenu · hover or ArrowRight">
            <Menu.Item leading={<EditIcon />}>Edit</Menu.Item>
            <Menu.Submenu leading={<FolderIcon />} label="Move to">
                <Menu.Item>Draft</Menu.Item>
                <Menu.Item>Sent</Menu.Item>
                <Menu.Item>Archived</Menu.Item>
            </Menu.Submenu>
            <Menu.Submenu label="Assign to" description="Pick who follows up">
                <Menu.Item>Daniel</Menu.Item>
                <Menu.Item>Unassigned</Menu.Item>
            </Menu.Submenu>
            <Menu.Separator />
            <Menu.Item leading={<TrashIcon />} destructive>
                Delete
            </Menu.Item>
        </OpenMenuCell>
        <OpenMenuCell label="Menu.RadioGroup · Sort by">
            <StatefulRadioGroup initial="date">
                <Menu.GroupLabel>Sort by</Menu.GroupLabel>
                <SortRadioItems />
            </StatefulRadioGroup>
        </OpenMenuCell>
        <OpenMenuCell label="Menu.ToggleItem · on / off (hover the second)">
            <StatefulToggleItem initial>Show voided</StatefulToggleItem>
            <StatefulToggleItem tooltip="Groups invoices under each customer">
                Group by customer
            </StatefulToggleItem>
        </OpenMenuCell>
        <OpenMenuCell label="disabled with reasons (hover them)">
            <Menu.Submenu
                label="Move to"
                disabled
                disabledReason="Posted invoices cannot move"
            >
                <Menu.Item>Draft</Menu.Item>
            </Menu.Submenu>
            <StatefulToggleItem
                initial
                disabled
                disabledReason="Set by your accountant"
            >
                Show voided
            </StatefulToggleItem>
            <StatefulToggleItem
                disabled
                disabledReason="Needs a customer column"
            >
                Group by customer
            </StatefulToggleItem>
            <Menu.Separator />
            <StatefulRadioGroup initial="name">
                <Menu.RadioItem value="name">Name</Menu.RadioItem>
                <Menu.RadioItem
                    value="amount"
                    disabled
                    disabledReason="Amounts are hidden for your role"
                >
                    Amount
                </Menu.RadioItem>
            </StatefulRadioGroup>
        </OpenMenuCell>
        <VariantItem label="live · radio closes, toggle stays open">
            <LiveSortMenu />
        </VariantItem>
    </VariantGrid>
);
